import React, { useState } from "react";
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from "@nextui-org/react";
import { groupFormDataBySubheadings, getSubheadingStructure } from "../../utils/formGrouping.utils";
import { StandardCloseButton } from "../../components/StandardButtons";

const GroupedDataPreview = ({ fieldInfoList, formData = {} }) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  // Convert fieldInfoList to formSchema format
  const formSchema = fieldInfoList.map(field => [
    field.Title,
    {
      type: field.Type,
      headingLevel: field.headingLevel,
      subheadingDescription: field.subheadingDescription,
      required: field.Required
    }
  ]);

  // Create sample data for preview if no real data exists
  const sampleData = {};
  fieldInfoList.forEach(field => {
    if (field.Type !== "subheading") {
      switch (field.Type) {
        case "text":
        case "textarea":
          sampleData[field.Title] = `Sample ${field.Title}`;
          break;
        case "integer":
          sampleData[field.Title] = 123;
          break;
        case "boolean":
          sampleData[field.Title] = true;
          break;
        case "dropdown":
          sampleData[field.Title] = "Option 1";
          break;
        case "file":
          sampleData[field.Title] = ["file1.pdf"];
          break;
        default:
          sampleData[field.Title] = `Sample value`;
      }
    }
  });

  const dataToUse = Object.keys(formData).length > 0 ? formData : sampleData;
  const groupedData = groupFormDataBySubheadings(dataToUse, formSchema);
  const structure = getSubheadingStructure(formSchema);

  return (
    <>
      <Button 
        size="sm" 
        variant="bordered" 
        onPress={onOpen}
        className="ml-2 border-slate-300 hover:border-slate-400 px-6 font-medium"
      >
        📊 Preview JSON Structure
      </Button>
      
      <Modal 
        isOpen={isOpen} 
        onOpenChange={onOpenChange}
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                JSON Data Structure Preview
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Form Structure</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      {structure.length > 0 ? (
                        structure.map((group, index) => (
                          <div key={index} className="mb-4 last:mb-0">
                            <div className="font-medium text-emerald-600 mb-1">
                              📁 {group.subheading.title}
                            </div>
                            {group.subheading.description && (
                              <div className="text-sm text-gray-500 mb-2">
                                {group.subheading.description}
                              </div>
                            )}
                            <div className="ml-4 space-y-1">
                              {group.fields.map((field, fieldIndex) => (
                                <div key={fieldIndex} className="text-sm">
                                  • {field.key} ({field.config.type})
                                  {field.config.required && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500">No subheadings defined yet</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-2">JSON Output Structure</h4>
                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto">
                      <pre className="text-sm">
                        {JSON.stringify(groupedData, null, 2)}
                      </pre>
                    </div>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h5 className="font-medium text-emerald-800 mb-2">Benefits of Grouped Structure:</h5>
                    <ul className="text-sm text-emerald-700 space-y-1">
                      <li>• Easy to understand data organization</li>
                      <li>• Fields grouped by logical sections</li>
                      <li>• Better API integration and data processing</li>
                      <li>• Improved data analysis and reporting</li>
                    </ul>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <StandardCloseButton onPress={onClose}>
                  Close
                </StandardCloseButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default GroupedDataPreview;